import { defineStore } from 'pinia';

import {
  login as loginAPI,
  logout as logoutAPI,
  getUser as getUserAPI,
  updateUser as updateUserAPI,
  verifyOtp as verifyOtpAPI,
} from '@/api/auth';
import { resetPassword as resetPasswordAPI } from '@/api/user-management.api';
import { getToken, removeToken, setToken } from '@/utils/cookies';
import {
  getTwoFactorState,
  setTwoFactorState,
  removeTwoFactorState,
} from '@/utils/twoFactor';
import { SUPER_ADMIN } from '@/constants/userGroups';
import { useModuleStore } from '@/store/module';
import { useVoipSocketStore } from '@/store/voipSocket';

export const useAuthStore = defineStore('auth', {
  state: () => {
    const tfaState = getTwoFactorState();
    return {
      user: null,
      token: getToken(),
      twoFactor: tfaState?.twoFactor || null,
      temporaryToken: tfaState?.temporaryToken || null,
    };
  },

  actions: {
    async login(credentials, router) {
      return await loginAPI(credentials).then(async (response) => {
        // Handle both wrapped and unwrapped responses
        const data = response?.data || response;
        const token = data?.token;
        const expires_in = data?.expires_in;
        const user = data?.user;
        const tfa = data?.tfa;
        const temporary_token = data?.temporary_token;
        const message = data?.message;

        // Check if 2FA is required
        if (
          message === 'Two Factor Authentication enforced.' ||
          message === 'OTP required.'
        ) {
          // Store temporary token in temp cookie only (not main), so direct URL / new tab cannot access protected routes
          this.$patch({
            temporaryToken: temporary_token,
            twoFactor: tfa || null,
          });
          removeToken(false);
          setToken(temporary_token, expires_in || 3600, true);
          setTwoFactorState(tfa || null, temporary_token);
          router.push({ name: 'VerifyOtp' });
          return;
        }

        // Direct login (2FA already verified)
        this.$patch({
          token: token,
          user: user,
          twoFactor: null,
          temporaryToken: null,
        });

        removeToken(true);
        setToken(token, expires_in);

        // Fetch full user data (including modules for sidebar menu) before navigating
        await this.getUser(true);

        // Redirect based on user group
        const isSuperAdmin = this.user?.user_group?.id === SUPER_ADMIN;
        const dashboardRoute = isSuperAdmin
          ? 'AdminDashboard'
          : 'AutoCallDashboard';
        router.push({ name: dashboardRoute });
      });
    },

    async verifyOtp(otp, router) {
      return await verifyOtpAPI({ otp }).then(async (response) => {
        const data = response?.data || response;
        const token = data?.token;
        const expires_in = data?.expires_in;
        const user = data?.user;

        this.$patch({
          token: token,
          user: user,
          twoFactor: null,
          temporaryToken: null,
        });

        removeToken(true);
        setToken(token, expires_in);
        removeTwoFactorState();

        // Fetch latest user data including modules
        await this.getUser(true);

        // Redirect based on user group
        const isSuperAdmin = this.user?.user_group?.id === SUPER_ADMIN;
        const dashboardRoute = isSuperAdmin
          ? 'AdminDashboard'
          : 'AutoCallDashboard';
        router.push({ name: dashboardRoute });
      });
    },

    async loginWithAuthKey(authKey) {
      return await loginAPI({ auth_key: authKey }).then(async (response) => {
        // Handle both wrapped and unwrapped responses
        const data = response?.data || response;
        const token = data?.token;
        const expires_in = data?.expires_in;
        const user = data?.user;

        this.$patch({
          token: token,
          user: user,
          twoFactor: null,
          temporaryToken: null,
        });

        removeToken(true);
        setToken(token, expires_in);
        removeTwoFactorState();

        // Fetch latest user data including modules
        await this.getUser(true);
      });
    },

    async logout(router) {
      await logoutAPI().finally(() => {
        removeToken();
        removeToken(true);
        removeTwoFactorState();

        useVoipSocketStore().disconnect();

        // Clear module cache on logout
        const moduleStore = useModuleStore();
        moduleStore.clearModules();

        this.$patch({
          user: null,
          token: null,
          twoFactor: null,
          temporaryToken: null,
        });

        router.push({ name: 'Login' });
      });
    },

    async getUser(force = false) {
      if (!force && this.user) {
        return this.user;
      }

      await getUserAPI()
        .then((response) => {
          // Handle both wrapped and unwrapped responses
          const userData = response?.user || response?.data?.user || response;
          this.$patch({
            user: userData,
          });
        })
        .catch((error) => {
          const response = error?.response;
          const requires2fa = response?.data?.requires_2fa;

          // If 2FA is required, move token to temp cookie and redirect to verify OTP (so middleware sees 2FA pending in any tab)
          if (response?.status === 403 && requires2fa) {
            const currentToken = getToken();
            removeToken();
            if (currentToken) {
              setToken(currentToken, 3600, true);
              setTwoFactorState(null, currentToken);
            }
            this.$patch({
              temporaryToken: currentToken || this.token,
              user: null,
            });
            window.location.href = '/verify-otp';
            return;
          }

          // For other errors, clear auth and redirect to login
          removeToken();
          removeTwoFactorState();
          this.$patch({
            user: null,
            token: null,
            temporaryToken: null,
            twoFactor: null,
          });
          window.location.href = '/login';
        });
    },
    async updateUser(updates) {
      try {
        const response = await updateUserAPI(updates);
        this.$patch((state) => {
          if (state.user) {
            Object.assign(state.user, updates);
          }
        });
        return response;
      } catch (error) {
        throw error;
      }
    },
    async resetUserPassword(updates) {
      try {
        const response = await resetPasswordAPI(updates);
        this.$patch((state) => {
          if (state.user) {
            Object.assign(state.user, updates);
          }
        });
        return response;
      } catch (error) {
        throw error;
      }
    },
  },
});
